// Preset-only date range select for analytics pages.
//
// Miluliem ships a richer DateRangePicker with a calendar for custom
// ranges (apps/admin/src/components/admin/DateRangePicker.tsx). Vīnoga
// reuses this lighter Select because the Dashboard already uses the
// same preset list and adding shadcn Calendar would be the only thing
// pulling in a new UI dep. Custom calendar lands as a follow-up.

import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type PresetKey =
  | "today"
  | "yesterday"
  | "last_7d"
  | "last_30d"
  | "this_month"
  | "last_month"
  | "this_year";

export interface DateRange {
  preset: PresetKey;
  /** ISO date YYYY-MM-DD — what the API expects. */
  from: string;
  /** ISO date YYYY-MM-DD inclusive. */
  to: string;
  label: string;
}

const PRESETS: Array<{ key: PresetKey; label: string }> = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last_7d", label: "Last 7 days" },
  { key: "last_30d", label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_year", label: "This year" },
];

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function presetLabel(key: PresetKey): string {
  return PRESETS.find((p) => p.key === key)?.label ?? key;
}

export function rangeForPreset(key: PresetKey, now: Date = new Date()): DateRange {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const minus = (days: number) => {
    const d = new Date(today);
    d.setDate(d.getDate() - days);
    return d;
  };
  switch (key) {
    case "today":
      return { preset: key, from: isoDate(today), to: isoDate(today), label: presetLabel(key) };
    case "yesterday": {
      const y = minus(1);
      return { preset: key, from: isoDate(y), to: isoDate(y), label: presetLabel(key) };
    }
    case "last_7d":
      return { preset: key, from: isoDate(minus(6)), to: isoDate(today), label: presetLabel(key) };
    case "last_30d":
      return { preset: key, from: isoDate(minus(29)), to: isoDate(today), label: presetLabel(key) };
    case "this_month": {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { preset: key, from: isoDate(start), to: isoDate(today), label: presetLabel(key) };
    }
    case "last_month": {
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      return { preset: key, from: isoDate(start), to: isoDate(end), label: presetLabel(key) };
    }
    case "this_year": {
      const start = new Date(today.getFullYear(), 0, 1);
      return { preset: key, from: isoDate(start), to: isoDate(today), label: presetLabel(key) };
    }
  }
}

export function rangeToQuery(range: DateRange): { from: string; to: string } {
  return { from: range.from, to: range.to };
}

export default function DateRangeSelect({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (r: DateRange) => void;
}) {
  const options = useMemo(() => PRESETS, []);
  return (
    <Select value={value.preset} onValueChange={(v) => onChange(rangeForPreset(v as PresetKey))}>
      <SelectTrigger className="h-8 w-[180px] font-body text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((p) => (
          <SelectItem key={p.key} value={p.key}>
            {p.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
